<template>
  <span>
    <b-button variant="link" class="list-view-icon-btn" v-b-tooltip.hover.bottom title="¿Cómo se usa?" aria-label="¿Cómo se usa?" @click="active = true">
      <AppIcon name="help" />
    </b-button>

    <b-modal v-model="active" size="lg" lazy>
      <div slot="modal-header" minimize class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="help" /></span>
        <h5 class="app-modal-header__title">Cómo usar Contabilidad</h5>
      </div>

      <h6 class="mb-1">En esta pantalla: {{ current.title }}</h6>
      <p class="text-muted small mb-2">{{ current.purpose }}</p>
      <ol class="pl-3 mb-3">
        <li v-for="(item, index) in current.steps" :key="index" class="mb-1">{{ item }}</li>
      </ol>

      <h6 class="mb-1">El circuito completo</h6>
      <p class="text-muted small mb-2">La contabilidad corre en paralelo a la gestión: facturar, comprar o cobrar nunca depende de ella.</p>
      <ol class="pl-3 mb-3">
        <li v-for="item in circuit" :key="item.key" class="mb-1" :class="{ 'font-weight-bold': item.key == view }">
          <router-link v-if="item.key != view" :to="{ name: item.route }" @click.native="active = false">{{ item.title }}</router-link>
          <span v-else>{{ item.title }}</span>
          <span class="font-weight-normal">: {{ item.summary }}</span>
        </li>
      </ol>

      <h6 class="mb-1">Para tener en cuenta</h6>
      <ul class="pl-3 mb-0">
        <li v-for="(item, index) in notes" :key="index" class="mb-1">{{ item }}</li>
      </ul>

      <div slot="modal-footer">
        <b-button variant="outline-secondary" size="sm" @click="active = false"><AppIcon name="close" class="mr-1" />Cerrar</b-button>
      </div>
    </b-modal>
  </span>
</template>

<script>
  const HELP = {
    accountPlan: {
      route: 'AccountingStaffAccountPlan',
      title: 'Plan de Cuentas',
      summary: 'cargar las cuentas.',
      purpose: 'Es la lista de cuentas que usan todos los asientos.',
      steps: [
        'Agregá las cuentas una por una, importalas desde el Gestor de Importaciones o, con el plan vacío, tocá "Cargar plan estándar", que además completa las cuentas generales.',
        'Indicá el tipo de cada cuenta: Activo, Pasivo, Patrimonio, Ingresos o Egresos. De eso depende cómo se calcula el cierre.',
        'Los asientos solo se imputan a cuentas sin subcuentas.',
      ],
    },
    settings: {
      route: 'AccountingStaffSettings',
      title: 'Configuración Contable',
      summary: 'indicar qué cuenta usa cada cosa.',
      purpose: 'Define las cuentas con las que se arman los asientos automáticos.',
      steps: [
        'En "Mis Empresas", revisá las empresas con las que trabajás.',
        'En "Cuentas Contables Por Defecto", elegí una cuenta para cada concepto.',
        'No te olvides de Resultado del Ejercicio: sin esa cuenta no se puede cerrar.',
      ],
    },
    accountingEntries: {
      route: 'AccountingStaffAccountingEntries',
      title: 'Movimientos Contables',
      summary: 'consultar o agregar asientos.',
      purpose: 'Muestra todos los asientos. Desde acá se cargan los asientos manuales.',
      steps: [
        'La primera apertura se carga en Cierre y Apertura. Acá se puede consultar y, si no tiene asientos posteriores, corregir o borrar.',
        'Elegí la empresa y el tipo (Declarado o No Declarado) de cada asiento.',
        'Para un ajuste, usá la clase "Común". El asiento tiene que balancear para poder guardarlo.',
        'En "Ver detalle" de un automático se ve la operación que lo originó y, si tiene comprobante, se puede abrir. Con el filtro Movimiento buscás por tipo de operación.',
        'Solo se editan los asientos manuales y de apertura. Un automático se corrige borrándolo y volviéndolo a aprobar desde Pendientes.',
      ],
    },
    accountingPending: {
      route: 'AccountingStaffAccountingPending',
      title: 'Asientos Pendientes',
      summary: 'revisar y aprobar los asientos de ventas, compras, recibos, pagos y fondos.',
      purpose: 'Lista los comprobantes sin asiento desde la última apertura. Sin apertura, no muestra nada.',
      steps: [
        'Tocá un comprobante para ver su asiento sugerido. Todavía no está guardado: se calcula con la configuración.',
        'Marcá los que dicen "Listo" y tocá "Aprobar seleccionados". Al aprobar, el asiento queda guardado y ya no cambia.',
        'Para corregir una sugerencia, tocá "Ver", editá y aprobá. Lo mismo con los que muestran un motivo en rojo.',
        'En "Para imputar", repartí el gasto de la compra entre las cuentas del proveedor, o elegí la contrapartida de un ajuste de caja.',
        'También aparecen los movimientos de fondos: ajustes, transferencias, gastos bancarios, depósitos y rechazos de cheques.',
        'Cada cheque lleva su asiento en el libro de su condición. En "Ver" podés cambiar el libro y las cuentas antes de aprobar.',
        'Repetí esto seguido: para cerrar el período no puede quedar ningún pendiente.',
      ],
    },
    accountingReports: {
      route: 'AccountingStaffAccountingReports',
      title: 'Reportes Contables',
      summary: 'sacar los libros y estados del ejercicio.',
      purpose: 'Libro Diario, Mayor, Sumas y Saldos, Estado de Resultados y Balance General. Solo incluyen lo aprobado.',
      steps: [
        'Elegí empresa, condición y período. Sin fecha de inicio, toma la última apertura.',
        'Elegí el reporte en las solapas. El Balance se calcula al corte, desde la apertura del ejercicio.',
        'Imprimí a PDF o exportá a Excel. La plantilla del PDF se edita en el módulo Reportes.',
        'Revisá el control debajo del reporte: Debe igual a Haber, y Activo igual a Pasivo más Patrimonio.',
      ],
    },
    accountingLedger: {
      route: 'AccountingStaffAccountingLedger',
      title: 'Mayor de Cuentas',
      summary: 'controlar saldos y movimientos.',
      purpose: 'Muestra los movimientos y saldos por cuenta. Es solo de consulta.',
      steps: [
        'Filtrá por período y cuenta para revisar lo que se imputó.',
        'Si algo no cierra, buscá el asiento en Movimientos Contables.',
        'Revisalo antes de cerrar, porque después el período queda bloqueado.',
      ],
    },
    accountingClosing: {
      route: 'AccountingStaffAccountingClosing',
      title: 'Cierre y Apertura de Ejercicio',
      summary: 'cerrar el período y abrir el siguiente.',
      purpose: 'Salda ingresos y egresos contra Resultado del Ejercicio y reabre las cuentas patrimoniales.',
      steps: [
        'Elegí empresa y tipo. Si todavía no hay apertura, tocá "Cargar apertura inicial" y después "Precargar cuentas": solo te queda completar los importes.',
        'Para cerrar, el inicio sale de la última apertura; ajustá la fecha de cierre y tocá "Previsualizar".',
        'Si aparece el aviso de movimientos sin asiento, generalos en Asientos Pendientes y volvé a previsualizar.',
        'Tocá "Generar cierre" y confirmá. Desde ese momento el período queda bloqueado.',
        'Tocá "Generar apertura". Esa fecha pasa a ser el nuevo inicio del control.',
        'Si hubo un error, "Reabrir período" deshace el último cierre con un motivo, siempre que el ejercicio siguiente no tenga asientos.',
        'Cada mes terminado, por ejemplo al presentar el IVA, usá "Bloqueo mensual": frena los cambios de ese mes y deja definitivos los números del Libro Diario. Requiere cero pendientes hasta esa fecha.',
        '"Desbloquear último mes" lo libera con un motivo. Antes de reabrir un cierre hay que desbloquear su mes.',
      ],
    },
  }

  const CIRCUIT = ['accountPlan', 'settings', 'accountingEntries', 'accountingPending', 'accountingLedger', 'accountingReports', 'accountingClosing']

  export default {
    props: {
      view: {
        type: String,
        required: true,
      },
    },
    data: () => {
      return {
        active: false,
        notes: [
          'Declarado y No Declarado se llevan por separado, cada uno con su apertura y su cierre.',
          'En un período cerrado o un mes bloqueado no se pueden cargar, editar ni borrar comprobantes o asientos.',
          'Un comprobante con asiento aprobado no se puede editar ni borrar. Primero hay que borrar el asiento en Movimientos Contables.',
          'Borrar un asiento automático devuelve el comprobante a Pendientes.',
        ],
      }
    },
    computed: {
      current() {
        return HELP[this.view]
      },
      circuit() {
        return CIRCUIT.map(key => Object.assign({ key }, HELP[key]))
      },
    },
  }
</script>
